import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Header from "../../components/Header";
import Link from "../../components/Link";
import PageTitle from "../../components/PageTitle";
import LoadingScreen from "../../components/LoadingScreen";
import CustomDropdown from "./components/CustomDropdown";
import Card from "./components/Card";
import styles from "../styles/estoques/Relatorio.module.css";

const API = "https://provin.ind.br/api";

const Relatorio = () => {
    const token = localStorage.getItem("token");
    const permissoes = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem("permissoes") || "{}");
        } catch {
            return {};
        }
    }, []);

    const empresasPermitidas = useMemo(() => {
        const arr = permissoes?.estoques?.empresas_permitidas;
        if (!Array.isArray(arr)) return [];
        return arr
            .map((x) => Number(x))
            .filter((n) => Number.isFinite(n));
    }, [permissoes]);

    const filtrosPermitidos = permissoes?.estoques?.relatorio?.filtros || {};
    const podeDownloadCSV = !!permissoes?.estoques?.relatorio?.download_csv;

    const [resumo, setResumo] = useState([]);
    const [totaisResumo, setTotaisResumo] = useState({ qtd_total: 0, valor_total: 0 });
    const [isLoading, setIsLoading] = useState(true);

    const [filtros, setFiltros] = useState({
        setor: [],
        regiao: [],
        empresa: [],
        nome_empresa: [],
        fabricante: [],
    });

    const [options, setOptions] = useState({
        setores: [],
        regioes: [],
        empresas: [],
        nome_empresas: [],
        fabricantes: [],
    });

    // Se só tem 1 empresa permitida, trava o filtro empresa nela (mantém dinâmica dos demais)
    useEffect(() => {
        if (empresasPermitidas.length === 1) {
            setFiltros((prev) => ({
                ...prev,
                empresa: [String(empresasPermitidas[0])],
            }));
        }
    }, [empresasPermitidas.length]);

    const axiosAuth = useMemo(() => {
        return axios.create({
            baseURL: API,
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
    }, [token]);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const resp = await axiosAuth.get("/estoques/relatorio/resumo", { params: filtros });

            // agora o back devolve { itens, totais }
            setResumo(Array.isArray(resp.data?.itens) ? resp.data.itens : []);
            setTotaisResumo({
                qtd_total: Number(resp.data?.totais?.qtd_total || 0),
                valor_total: Number(resp.data?.totais?.valor_total || 0),
            });
        } catch (error) {
            console.error("Erro ao buscar resumo:", error);
            setResumo([]);
            setTotaisResumo({ qtd_total: 0, valor_total: 0 });
        } finally {
            setIsLoading(false);
        }
    };

    const fetchOptions = async () => {
        try {
            const resp = await axiosAuth.get("/estoques/relatorio/optionsSelects", {
                params: filtros,
            });

            const data = resp.data || {
                setores: [],
                regioes: [],
                empresas: [],
                nome_empresas: [],
                fabricantes: [],
            };

            // UX extra (back-end já restringe, mas isso garante)
            if (empresasPermitidas.length) {
                data.empresas = (data.empresas || []).filter((e) =>
                    empresasPermitidas.includes(Number(e))
                );
            }

            setOptions({
                setores: data.setores || [],
                regioes: data.regioes || [],
                empresas: data.empresas || [],
                nome_empresas: data.nome_empresas || [],
                fabricantes: data.fabricantes || [],
            });
        } catch (err) {
            console.error("Erro ao carregar filtros:", err);
            setOptions({
                setores: [],
                regioes: [],
                empresas: [],
                nome_empresas: [],
                fabricantes: [],
            });
        }
    };

    // 🔁 Sempre que filtros mudarem, executa a pesquisa e atualiza options (mantém seu comportamento dinâmico)
    useEffect(() => {
        fetchData();
        fetchOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtros]);

    // Primeira carga
    useEffect(() => {
        fetchData();
        fetchOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = (e) => {
        if (e?.preventDefault) e.preventDefault();
        fetchData();
    };

    const limparFiltros = () => {
        setFiltros({
            setor: [],
            regiao: [],
            // se só tem 1 empresa permitida, mantém ela; senão, limpa
            empresa: empresasPermitidas.length === 1 ? [String(empresasPermitidas[0])] : [],
            nome_empresa: [],
            fabricante: [],
        });
    };

    // Export CSV com autenticação (não pode window.open porque não manda Authorization)
    const exportarCSV = async () => {
        try {
            const resp = await axiosAuth.get("/estoques/relatorio/exportar", {
                params: filtros,
                responseType: "blob",
            });

            const blob = new Blob([resp.data], { type: "text/csv;charset=utf-8;" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "estoque_completo.csv";
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Erro ao exportar CSV:", error);
        }
    };

    const mostrarAreaFiltros = empresasPermitidas.length > 1;

    return (
        <div className={styles.body}>
            <Header />
            <Link redirectTo="/estoques" />
            <PageTitle src="/images/common/relatorio.ico" title="Relatório de Estoques" />

            <div className={styles.container}>
                {mostrarAreaFiltros && (
                    <>
                        <form className={styles.filtros} onSubmit={handleSubmit}>
                            {filtrosPermitidos.setor && (
                                <CustomDropdown
                                    label="Setor"
                                    options={options.setores}
                                    value={filtros.setor}
                                    multiple
                                    onChange={(val) => setFiltros((prev) => ({ ...prev, setor: val }))}
                                />
                            )}

                            {filtrosPermitidos.regiao && (
                                <CustomDropdown
                                    label="Região"
                                    options={options.regioes}
                                    value={filtros.regiao}
                                    multiple
                                    onChange={(val) => setFiltros((prev) => ({ ...prev, regiao: val }))}
                                />
                            )}

                            {/* Empresa: se só tem 1 permitida, você pode esconder. Se quiser mostrar mesmo assim, remova o "mostrarAreaFiltros" daqui */}
                            {filtrosPermitidos.empresa && empresasPermitidas.length > 1 && (
                                <CustomDropdown
                                    label="ID da Empresa"
                                    options={options.empresas}
                                    value={filtros.empresa}
                                    multiple
                                    onChange={(val) => setFiltros((prev) => ({ ...prev, empresa: val }))}
                                />
                            )}

                            {filtrosPermitidos.nome_empresa && (
                                <CustomDropdown
                                    label="Nome da Empresa"
                                    options={options.nome_empresas}
                                    value={filtros.nome_empresa}
                                    multiple
                                    onChange={(val) => setFiltros((prev) => ({ ...prev, nome_empresa: val }))}
                                />
                            )}

                            {filtrosPermitidos.fabricante && (
                                <CustomDropdown
                                    label="Fabricante dos Produtos"
                                    options={options.fabricantes}
                                    value={filtros.fabricante}
                                    multiple
                                    onChange={(val) => setFiltros((prev) => ({ ...prev, fabricante: val }))}
                                />
                            )}
                        </form>

                        <div className={styles.limparFiltros}>
                            <button type="button" onClick={limparFiltros}>
                                🧹 Limpar Filtros
                            </button>
                        </div>
                    </>
                )}

                <div className={styles.totaisWrap}>
                    <p className={styles.titulo}>Total Geral</p>

                    <div className={styles.grid}>
                        <div className={styles.item}>
                            <span className={styles.label}>Qtd total</span>
                            <span className={styles.valor}>{totaisResumo.qtd_total}</span>
                        </div>

                        <div className={styles.item}>
                            <span className={styles.label}>Valor total</span>
                            <span className={styles.valor}>
                                {totaisResumo.valor_total?.toLocaleString("pt-BR", {
                                    style: "currency",
                                    currency: "BRL",
                                })}
                            </span>
                        </div>
                    </div>
                </div>

                <div className={styles.buttons}>
                    <button
                        type="button"
                        onClick={() => window.print()}
                        className={styles.botaoImprimir}
                    >
                        🖨️ Imprimir
                    </button>

                    {podeDownloadCSV && (
                        <button
                            type="button"
                            onClick={exportarCSV}
                            className={styles.botaoExportarCSV}
                        >
                            📄 Exportar CSV
                        </button>
                    )}
                </div>

                {resumo.map((empresa, idx) => (
                    <Card key={idx} empresa={empresa} />
                ))}
            </div>

            {isLoading && <LoadingScreen />}
        </div>
    );
};

export default Relatorio;
